import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CITIES, DISCIPLINES, STANCES, LEVELS, FIGHT_STATUS } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Camera, Loader2, Save } from "lucide-react";

const field = "bg-[#0d0d0d] border-[#27272a] text-white h-11 focus:ring-2 focus:ring-volt focus:border-volt";

// Downscale + convert to JPEG in the browser so large phone photos upload reliably.
function compressImage(file) {
  return new Promise((resolve) => {
    if (!file.type || !file.type.startsWith("image/") || file.type === "image/gif") {
      return resolve(file);
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const maxDim = 1400;
      if (width > maxDim || height > maxDim) {
        const s = maxDim / Math.max(width, height);
        width = Math.round(width * s);
        height = Math.round(height * s);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return resolve(file);
          const name = (file.name.replace(/\.[^.]+$/, "") || "avatar") + ".jpg";
          resolve(new File([blob], name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        0.85,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // e.g. HEIC the browser can't decode — send original, backend accepts it
    };
    img.src = url;
  });
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className={field}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#161616] border-[#27272a] text-white">
          {options.map((o) => (
            <SelectItem key={o} value={o} className="focus:bg-volt focus:text-black">{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function ProfilePanel({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  // Sync the form from the current user each time the dialog opens
  useEffect(() => {
    if (open && user) {
      setForm({
        fighter_name: user.fighter_name || "",
        gym_name: user.gym_name || "",
        city: user.city || "",
        discipline: user.discipline || "",
        stance: user.stance || "",
        weight: String(user.weight ?? ""),
        weight_unit: user.weight_unit || "kg",
        height: user.height || "",
        experience_level: user.experience_level || "",
        fight_status: user.fight_status || "Active",
        avatar_url: user.avatar_url || null,
      });
    }
  }, [open, user]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const prepared = await compressImage(file);
      const fd = new FormData();
      fd.append("file", prepared);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, avatar_url: data.url }));
      toast.success("Photo uploaded. Hit save to keep it.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, weight: parseFloat(form.weight) };
      const { data } = await api.patch("/profile", payload);
      setUser(data.user);
      toast.success("Profile updated.");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="profile-panel"
        className="bg-[#161616] border-[#27272a] text-white max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl uppercase tracking-tight">Your Profile</DialogTitle>
          <DialogDescription className="text-zinc-400">Update your fighter details and photo.</DialogDescription>
        </DialogHeader>

        {/* Avatar */}
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl overflow-hidden border border-[#27272a] bg-[#0d0d0d] flex items-center justify-center shrink-0">
            {form.avatar_url ? (
              <img src={resolveImg(form.avatar_url)} alt="avatar" className="w-full h-full object-cover" />
            ) : (
              <span className="font-heading text-4xl text-zinc-700">{form.fighter_name?.[0] || "?"}</span>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} data-testid="avatar-file-input" />
            <button
              data-testid="avatar-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 border border-[#27272a] text-white text-sm uppercase tracking-wider px-4 py-2 rounded-xl hover:border-volt hover:text-volt transition-colors duration-200 disabled:opacity-50"
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
              {uploading ? "Uploading…" : "Change Photo"}
            </button>
            <p className="text-xs text-zinc-600 mt-1.5">JPG, PNG, WEBP or HEIC · max 15MB</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Fighter Name</Label>
            <Input data-testid="profile-name" value={form.fighter_name} onChange={(e) => set("fighter_name")(e.target.value)} className={field} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Gym</Label>
            <Input data-testid="profile-gym" value={form.gym_name} onChange={(e) => set("gym_name")(e.target.value)} className={field} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="City / Region" value={form.city} onChange={set("city")} options={CITIES} testid="profile-city" />
          <SelectField label="Discipline" value={form.discipline} onChange={set("discipline")} options={DISCIPLINES} testid="profile-discipline" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Stance" value={form.stance} onChange={set("stance")} options={STANCES} testid="profile-stance" />
          <SelectField label="Experience" value={form.experience_level} onChange={set("experience_level")} options={LEVELS} testid="profile-level" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Weight</Label>
            <Input data-testid="profile-weight" type="number" value={form.weight} onChange={(e) => set("weight")(e.target.value)} className={field} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Unit</Label>
            <Select value={form.weight_unit} onValueChange={set("weight_unit")}>
              <SelectTrigger className={field}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#161616] border-[#27272a] text-white">
                <SelectItem value="kg" className="focus:bg-volt focus:text-black">kg</SelectItem>
                <SelectItem value="lbs" className="focus:bg-volt focus:text-black">lbs</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Height</Label>
            <Input data-testid="profile-height" value={form.height} onChange={(e) => set("height")(e.target.value)} className={field} />
          </div>
        </div>
        <SelectField label="Fight Status" value={form.fight_status} onChange={set("fight_status")} options={FIGHT_STATUS} testid="profile-status" />

        <button
          data-testid="profile-save-btn"
          onClick={save}
          disabled={saving}
          className="w-full bg-volt text-black font-heading font-bold uppercase tracking-widest py-3 rounded-xl transition-colors duration-200 hover:bg-volt-hover disabled:opacity-40 flex items-center justify-center gap-2 mt-2"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
